import { useState } from 'react'

import { Typography } from '@mui/material'
import { isNil } from 'es-toolkit'

import { api } from '@/api'
import { MfaLevel, TwoFactorAuthType, TwoFactorEnforcementState } from '@/api/types'
import { authService } from '@/services/auth-service'
import { app } from '@/states/app-state'
import { AuthErrorCode, isErrorCode } from '@/utils/error'

import { SendMFACodeForm } from './send-mfa-code-form'
import { VerifyMFACodeForm, type VerifyMFACodeFormProps } from './verify-mfa-code-form'

type ViewType = 'sign-in' | 'enroll' | 'change-auth-method' | 'step-up' | 'reset-password'

interface Props extends Pick<VerifyMFACodeFormProps, 'onVerify'> {
  viewType: ViewType
  // Defaults come from the login token; full-session callers pass them or let the form load the details.
  allowedMethods?: TwoFactorAuthType[]
  hasMfaMethod?: boolean
  method?: TwoFactorAuthType
  // Shown above the send step only; once a code is out, the page is about entering it.
  intro?: string
  onUseRecoveryCode?: VoidFunction
  onEnrollAuthenticator?: VoidFunction
  // A token issued before the cutoff can still point at email, which the server now refuses.
  onEmailRetired?: VoidFunction
}

type Step = 'send-code' | 'validate-code'

const PHONE_METHODS: TwoFactorAuthType[] = [TwoFactorAuthType.Sms, TwoFactorAuthType.Call]

const sendCodeMessageTextMap: Record<ViewType, string> = {
  'sign-in': 'Choose how to receive your verification code.',
  enroll: 'Choose how you want to verify it is you when you sign in.',
  'change-auth-method': 'Change authentication method',
  'step-up': 'Choose how to receive your verification code.',
  'reset-password': 'Choose how to receive your verification code.',
}

const sendCodeButtonTextMap: Record<ViewType, string> = {
  'sign-in': 'Send Code',
  enroll: 'Send Code',
  'change-auth-method': 'Change Method',
  'step-up': 'Send Code',
  'reset-password': 'Send Code',
}

const verifyCodeButtonTextMap: Record<ViewType, string> = {
  'sign-in': 'Sign In',
  enroll: 'Verify Code',
  'change-auth-method': 'Verify Code',
  'step-up': 'Verify',
  'reset-password': 'Continue',
}

// Older tokens carry only the level; the server allows everything at Phone and only the app at Authenticator.
function allowedMethodsForLevel(level?: MfaLevel) {
  if (!isNil(level) && level >= MfaLevel.Authenticator) return [TwoFactorAuthType.Authenticator]
  return [TwoFactorAuthType.Sms, TwoFactorAuthType.Call, TwoFactorAuthType.Authenticator]
}

// An enrolled user proves the method they already have; text and call share one phone, so they stay together.
function methodsToProve(method: TwoFactorAuthType) {
  return PHONE_METHODS.includes(method) ? PHONE_METHODS : [method]
}

export function MfaVerification({
  viewType,
  allowedMethods,
  hasMfaMethod,
  method,
  intro,
  onVerify,
  onUseRecoveryCode,
  onEnrollAuthenticator,
  onEmailRetired,
}: Props) {
  const decodedToken = authService.getDecodedToken()

  const isEnrolling = viewType === 'enroll' || viewType === 'change-auth-method'
  const hasMethod = hasMfaMethod ?? decodedToken?.HasMfaMethod ?? false

  const tokenAllowed = decodedToken?.AllowedMfaMethods.length
    ? decodedToken.AllowedMfaMethods
    : allowedMethodsForLevel(decodedToken?.MinMfaLevel)
  const baseAllowed = allowedMethods ?? tokenAllowed

  const initialMethod = method ?? decodedToken?.TwoFactorType
  // Email still proves a sign-in until the enforcement date; password reset and step-up never accept it.
  const provesEmail = initialMethod === TwoFactorAuthType.Email && viewType === 'sign-in'
  const isProvingExistingMethod =
    hasMethod && !isEnrolling && !isNil(initialMethod) && (initialMethod !== TwoFactorAuthType.Email || provesEmail)
  // Enforcement off and the company still takes email: email leads, text or call may be picked instead.
  const offerEmailChoice =
    viewType === 'sign-in' &&
    hasMethod &&
    decodedToken?.MfaEnforcementState === TwoFactorEnforcementState.Off &&
    baseAllowed.includes(TwoFactorAuthType.Email) &&
    initialMethod !== TwoFactorAuthType.Authenticator
  const effectiveAllowed = isProvingExistingMethod && !offerEmailChoice ? methodsToProve(initialMethod) : baseAllowed
  // Picking text or call without a confirmed phone needs a number to send to; an old unconfirmed one may be wrong.
  const allowEditPhoneNumber = isEnrolling || (offerEmailChoice && !decodedToken?.PhoneNumberConfirmed)
  const startsAtCode = isProvingExistingMethod && initialMethod === TwoFactorAuthType.Authenticator

  const [step, setStep] = useState<Step>(startsAtCode ? 'validate-code' : 'send-code')
  const [currentMethod, setCurrentMethod] = useState<TwoFactorAuthType>(
    initialMethod ?? (offerEmailChoice ? TwoFactorAuthType.Email : TwoFactorAuthType.Sms),
  )
  const [otpSentTo, setOtpSentTo] = useState<string | null>()

  const backToSend = () => {
    setStep('send-code')
    setOtpSentTo(null)
  }

  const resendCode = api.useSendTwoFactorAuthCode({
    mutation: {
      onSuccess: () => app.showMessageToast({ variant: 'success', message: 'A new code was sent' }),
      onError: (error) => {
        if (isErrorCode(error, AuthErrorCode.EmailRetired)) onEmailRetired?.()
      },
    },
  })

  // Proving a method already on file has nothing to re-choose, so resend just sends again; enrolment may have a new number.
  const resend = () => {
    if (!isProvingExistingMethod || allowEditPhoneNumber) return backToSend()
    if (resendCode.isPending) return
    resendCode.mutate({ data: { twoFactorAuthType: currentMethod, phoneNumber: null } })
  }

  return (
    <>
      {step === 'send-code' && !!intro && <Typography variant="body2">{intro}</Typography>}
      {step === 'send-code' && (
        <SendMFACodeForm
          allowedMethods={effectiveAllowed}
          defaultMethod={currentMethod}
          allowRetrieveAuthDetails={viewType === 'change-auth-method' || viewType === 'step-up'}
          provingExistingMethod={isProvingExistingMethod}
          allowEditPhoneNumber={allowEditPhoneNumber}
          allowEmailMethod={viewType !== 'step-up' && viewType !== 'reset-password'}
          offerEmailChoice={offerEmailChoice}
          sendCodeMessageText={sendCodeMessageTextMap[viewType]}
          sendCodeButtonText={sendCodeButtonTextMap[viewType]}
          onCodeSent={(sentMethod, sentTo) => {
            setCurrentMethod(sentMethod)
            setOtpSentTo(sentTo)
            setStep('validate-code')
          }}
          onEnrollAuthenticator={onEnrollAuthenticator}
          onEmailRetired={onEmailRetired}
        />
      )}
      {step === 'validate-code' && (
        <VerifyMFACodeForm
          method={currentMethod}
          otpSentTo={otpSentTo}
          allowUpdateRememberBrowser={viewType === 'sign-in'}
          verifyCodeButtonText={verifyCodeButtonTextMap[viewType]}
          onVerify={onVerify}
          onResendClick={currentMethod === TwoFactorAuthType.Authenticator ? undefined : resend}
          onChangeMethodClick={currentMethod === TwoFactorAuthType.Authenticator ? undefined : backToSend}
          onUseRecoveryCode={currentMethod === TwoFactorAuthType.Authenticator ? onUseRecoveryCode : undefined}
        />
      )}
    </>
  )
}
